import { assertAttributeUsageJSON, AttributeUsageModel, BufferAttributeModel, common, ECPointAttributeModel, scriptHashToAddress, TransactionTypeModel, UInt160AttributeModel, UInt256AttributeModel, utils, VMState, } from '@neo-one/client-common';
import BigNumber from 'bignumber.js';
import _ from 'lodash';
import { interval } from 'rxjs';
import { filter, switchMap, take, timeout } from 'rxjs/operators';
import { NeoNotImplementedError, UnknownTransactionError } from '../errors';
import { convertCallReceipt, convertInput, convertInputs, convertOutput, convertRelayTransactionResult, convertTransactionBase, } from './convert';
import { DataProviderBase } from './DataProviderBase';
export class NEODataProvider extends DataProviderBase {
    async executeGetOutput(input) {
        const transaction = await this.getTransaction(input.hash);
        return transaction.outputs[input.index];
    }
    async executeTestInvoke(transaction) {
        const scriptHashesForVerifying = await transaction.getScriptHashesForVerifying({
            getOutput: this.getOutput,
            getAsset: this.getAsset,
        });
        const result = await this.mutableClient.testInvokeRaw(transaction.script.toString('hex'), scriptHashesForVerifying);
        const gasConsumed = new BigNumber(result.gas_consumed).lt(utils.TEN_BIG_NUMBER)
            ? '0'
            : new BigNumber(result.gas_consumed).minus(utils.TEN_BIG_NUMBER).toString();
        if (result.state === 'HALT') {
            return convertCallReceipt({
                result: {
                    state: VMState.Halt,
                    gas_consumed: gasConsumed,
                    gas_cost: result.gas_consumed,
                    stack: result.stack,
                },
                actions: [],
            });
        }
        return convertCallReceipt({
            result: {
                state: VMState.Fault,
                gas_consumed: gasConsumed,
                gas_cost: result.gas_consumed,
                stack: result.stack,
                message: '',
            },
            actions: [],
        });
    }
    async executeGetTransactionReceipt(hash, options) {
        let watchTimeoutMS = 120000;
        if (options !== undefined && options.timeoutMS !== undefined) {
            watchTimeoutMS = options.timeoutMS;
        }
        let height;
        try {
            height = await interval(5000)
                .pipe(switchMap(async () => {
                try {
                    const result = await this.mutableClient.getTransactionHeight(hash);
                    return result;
                }
                catch {
                    return undefined;
                }
            }), filter((transactionHeight) => transactionHeight !== undefined), take(1), timeout(new Date(Date.now() + watchTimeoutMS)))
                .toPromise();
        }
        catch {
            throw new UnknownTransactionError(hash);
        }
        if (height === undefined) {
            throw new UnknownTransactionError(hash);
        }
        const block = await this.getBlock(height);
        return {
            blockIndex: height,
            blockHash: block.hash,
            transactionIndex: block.transactions.findIndex((transaction) => common.uInt256Equal(common.hexToUInt256(transaction.hash), common.hexToUInt256(hash))),
            globalIndex: new BigNumber(-1),
        };
    }
    async executeRelayTransaction(transaction, networkFee = utils.ZERO_BIG_NUMBER) {
        const transactionSerialized = transaction.serializeWire();
        await this.mutableClient.sendRawTransaction(transactionSerialized.toString('hex'));
        const transactionBase = {
            txid: transaction.hashHex,
            size: transactionSerialized.byteLength,
            version: transaction.version,
            attributes: transaction.attributes.map(this.convertAttributeModel),
            vin: transaction.inputs.map((input) => ({
                txid: input.hashHex,
                vout: input.index,
            })),
            vout: transaction.outputs.map((output, n) => ({
                n,
                asset: common.uInt256ToHex(output.asset),
                value: output.value.toString(),
                address: scriptHashToAddress(common.uInt160ToHex(output.address)),
            })),
            scripts: transaction.scripts.map((script) => ({
                invocation: script.invocation.toString('hex'),
                verification: script.verification.toString('hex'),
            })),
            sys_fee: utils.ZERO.toString(),
            net_fee: networkFee.toString(),
            data: undefined,
        };
        if (transaction.type === TransactionTypeModel.Claim) {
            return convertRelayTransactionResult({
                transaction: {
                    ...transactionBase,
                    type: 'ClaimTransaction',
                    claims: transaction.claims.map((input) => ({
                        txid: input.hashHex,
                        vout: input.index,
                    })),
                },
            });
        }
        if (transaction.type === TransactionTypeModel.Invocation) {
            return convertRelayTransactionResult({
                transaction: {
                    ...transactionBase,
                    type: 'InvocationTransaction',
                    script: transaction.script.toString('hex'),
                    gas: transaction.gas.toString(),
                },
            });
        }
        throw new NeoNotImplementedError('Transaction types besides Claim & Invocation are');
    }
    async executeGetUnspentOutputs(address) {
        const unspents = await this.mutableClient.getUnspents(address);
        return _.flatten(unspents.balance.map(({ unspent, asset_hash }) => unspent.map(({ txid, n, value }) => {
            const output = convertOutput({ address, asset: common.add0x(asset_hash), value, n });
            const input = convertInput({ txid, vout: n });
            return {
                ...input,
                ...output,
            };
        })));
    }
    async executeGetUnclaimed(address) {
        const claimable = await this.mutableClient.getClaimable(address);
        return {
            unclaimed: convertInputs(claimable.claimable.map(({ txid, n }) => ({ txid, vout: n }))),
            amount: new BigNumber(claimable.unclaimed),
        };
    }
    async executeGetAccountInternal(address) {
        const [account, { claimable }, unspents] = await Promise.all([
            this.mutableClient.getAccount(address),
            this.mutableClient.getClaimable(address),
            this.mutableClient.getUnspents(address),
        ]);
        return {
            version: account.version,
            script_hash: account.script_hash,
            frozen: account.frozen,
            votes: account.votes,
            balances: account.balances,
            unspent: _.flatten(unspents.balance.map(({ unspent }) => unspent.map(({ txid, n }) => ({ txid, vout: n })))),
            unclaimed: claimable.map(({ txid, n }) => ({ txid, vout: n })),
        };
    }
    convertConfirmedTransaction(transaction, block) {
        const data = {
            blockHash: block.hash,
            blockIndex: block.index,
            transactionIndex: block.tx.findIndex((tx) => tx.txid === transaction.txid),
            globalIndex: new BigNumber(-1),
        };
        return convertTransactionBase(transaction, (invocation, transactionBase) => ({
            ...transactionBase,
            type: 'InvocationTransaction',
            script: invocation.script,
            gas: new BigNumber(invocation.gas),
            receipt: data,
        }), (converted) => ({ ...converted, receipt: data }));
    }
    convertAttributeModel(attribute) {
        const usage = assertAttributeUsageJSON(AttributeUsageModel[attribute.usage]);
        if (attribute instanceof UInt160AttributeModel) {
            return {
                usage,
                data: common.uInt160ToHex(attribute.value),
            };
        }
        if (attribute instanceof UInt256AttributeModel) {
            return {
                usage,
                data: common.uInt256ToHex(attribute.value),
            };
        }
        if (attribute instanceof ECPointAttributeModel) {
            return {
                usage,
                data: common.ecPointToHex(attribute.value),
            };
        }
        if (attribute instanceof BufferAttributeModel) {
            return {
                usage,
                data: attribute.value.toString('hex'),
            };
        }
        throw new Error('For TS');
    }
}

//# sourceMappingURL=NEODataProvider.js.map
